import React, { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import PageHero from "../components/ui-custom/PageHero";
import GlassCard from "../components/ui-custom/GlassCard";
import Reveal from "../components/ui-custom/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { contactInfo } from "../data/mock";

const initialForm = { name: "", email: "", phone: "", subject: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    // Frontend-only mock submission — no backend connected yet.
    setTimeout(() => {
      setSubmitting(false);
      toast.success("Enquiry sent! Our team will get back to you shortly.");
      setForm(initialForm);
    }, 900);
  };

  return (
    <div>
      <PageHero eyebrow="Contact" title="Contact Us" subtitle="Reach out for business enquiries, partnerships, or media requests." />
      <section className="px-6 pb-28">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
          <Reveal>
            <GlassCard className="p-8 h-full flex flex-col">
              <h3 className="font-display font-semibold text-foreground text-lg mb-1">Registered &amp; Corporate Office</h3>
              <p className="text-sm text-qamber mb-6">{contactInfo.officeName}</p>
              <div className="space-y-5 text-sm text-muted-foreground">
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-qcyan mt-0.5 shrink-0" />
                  <span>{contactInfo.addressLine}</span>
                </div>
                <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-3 hover:text-qcyan transition-colors">
                  <Phone size={18} className="text-qcyan shrink-0" />
                  <span>{contactInfo.phone}</span>
                </a>
                <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-3 hover:text-qcyan transition-colors">
                  <Mail size={18} className="text-qcyan shrink-0" />
                  <span>{contactInfo.email}</span>
                </a>
                <div className="flex items-center gap-3">
                  <Clock size={18} className="text-qcyan shrink-0" />
                  <span>{contactInfo.hours}</span>
                </div>
              </div>
            </GlassCard>
          </Reveal>

          <Reveal delay={0.1}>
            <GlassCard className="p-8">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="name" className="text-muted-foreground">Full Name</Label>
                  <Input id="name" name="name" value={form.name} onChange={handleChange} className="mt-1.5 bg-white/5 border-border/10 text-foreground focus-visible:ring-qcyan" placeholder="Your name" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="email" className="text-muted-foreground">Email</Label>
                    <Input id="email" type="email" name="email" value={form.email} onChange={handleChange} className="mt-1.5 bg-white/5 border-border/10 text-foreground focus-visible:ring-qcyan" placeholder="you@company.com" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-muted-foreground">Phone</Label>
                    <Input id="phone" name="phone" value={form.phone} onChange={handleChange} className="mt-1.5 bg-white/5 border-border/10 text-foreground focus-visible:ring-qcyan" placeholder="+91" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="subject" className="text-muted-foreground">Subject</Label>
                  <Input id="subject" name="subject" value={form.subject} onChange={handleChange} className="mt-1.5 bg-white/5 border-border/10 text-foreground focus-visible:ring-qcyan" placeholder="Business enquiry" />
                </div>
                <div>
                  <Label htmlFor="message" className="text-muted-foreground">Message</Label>
                  <Textarea id="message" name="message" value={form.message} onChange={handleChange} rows={4} className="mt-1.5 bg-white/5 border-border/10 text-foreground focus-visible:ring-qcyan" placeholder="Tell us about your requirement" />
                </div>
                <Button type="submit" disabled={submitting} className="btn-glow-primary w-full py-6 rounded-md font-semibold tracking-wide inline-flex items-center justify-center gap-2">
                  {submitting ? "Sending..." : "Send Message"} <Send size={16} />
                </Button>
              </form>
            </GlassCard>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
